import {
  MedicationType,
  MedicationTypeDisplay,
  MedicationTypeValue,
  type ObjectType,
} from '@quromedical/fhir-common';
import { CrudForm } from 'components/form';
import type { Field, PrimitiveValueType, SelectOption } from 'components/types';
import { mapDisplaysToOptions } from 'core/forms';
import type { AsyncFetcher } from 'design-system';
import { useCallback, useState } from 'react';
import { strings } from 'strings';
import * as yup from 'yup';

import { mapMedicationRepeatToRepeatName } from './internal';
import {
  EnumMedicationRepeat,
  type MedicationUsageFormData,
  medicationFrequencyDisplay,
  RepeatDescription,
} from './types';

export type MedicationFetcher = AsyncFetcher<PrimitiveValueType, any>;

interface FormProps {
  title?: string;
  medicationFetcher: MedicationFetcher;
  onAdd: () => void;
  onChange: (
    data: Partial<MedicationUsageFormData>,
    hasErrors: boolean,
  ) => void;
  onRemove: () => void;
  canAdd: boolean;
  canRemove: boolean;
  showErrors: boolean;
  initialValues: Partial<MedicationUsageFormData>;
  isDuplicated?: boolean;
}

const MedicationTypeFetcher = [
  {
    display: MedicationTypeDisplay[MedicationType.chronic],
    value: MedicationType.chronic,
  },
  {
    display: MedicationTypeDisplay[MedicationType.acute],
    value: MedicationType.acute,
  },
];

const MedicationRepeatCodes = Object.values(EnumMedicationRepeat);

type FormData = {
  medication: SelectOption<any, string>;
  /**
   * represents a repeatData
   */
  repeat: EnumMedicationRepeat;
  noteText?: string;
  type: MedicationType;
};

export const schema = yup
  .object<ObjectType<FormData>>({
    medication: yup.object().required(),
    repeat: yup.string().oneOf(MedicationRepeatCodes).required(),
    noteText: yup.string().notRequired(),
  })
  .required();

const createFields = (
  medicationFetcher: MedicationFetcher,
): Field<FormData>[] => [
  {
    subfields: [
      {
        key: 'medication',
        label: strings.LabelMedication,
        initialSelection: [],
        searchable: true,
        type: 'combobox-single',
        fetcher: medicationFetcher,
        returnFullOption: true,
      },
      {
        key: 'repeat',
        label: strings.LabelMedicationFrequency,
        type: 'combobox-single',
        fetcher: mapDisplaysToOptions(medicationFrequencyDisplay),
      },
      {
        type: 'combobox-single',
        label: strings.LabelMedicationType,
        fetcher: MedicationTypeFetcher,
        key: 'type',
      },
      {
        type: 'text-box',
        key: 'noteText',
        label: strings.LabelMedicationNoteText,
      },
    ],
  },
];

const mapInitialValuesToFormData = (
  initialValues: Partial<MedicationUsageFormData>,
): Partial<FormData> => ({
  ...initialValues,
  type: initialValues.type
    ? MedicationTypeValue[initialValues.type]
    : undefined,
  repeat: mapMedicationRepeatToRepeatName({
    frequency: initialValues.frequency,
    period: initialValues.period,
    periodUnit: initialValues.periodUnit,
  }),
});

export const MedicationForm = ({
  title,
  medicationFetcher,
  onAdd,
  onChange,
  onRemove,
  canAdd,
  canRemove,
  showErrors,
  initialValues,
  isDuplicated = false,
}: FormProps) => {
  const [medicationDisplay, setMedicationDisplay] = useState(title);

  const handleChange = useCallback(
    (data: Partial<FormData>) => {
      const medication = data.medication;
      const repeatData = data.repeat ? RepeatDescription[data.repeat] : {};

      // manually check schema since the internal form does not validate if not touched
      const isValid = schema.isValidSync(data);
      setMedicationDisplay(medication?.display || title);
      onChange(
        {
          medicationCode: medication?.value,
          noteText: data.noteText,
          type: data.type,
          ...repeatData,
        },
        !isValid,
      );
    },
    [onChange, title],
  );

  const initialData = mapInitialValuesToFormData(initialValues);

  const onSubmit = useCallback(() => {
    onAdd();
    return {};
  }, [onAdd]);

  const fields = createFields(medicationFetcher);

  return (
    <CrudForm<FormData>
      title={medicationDisplay}
      validationSchema={schema}
      fields={fields}
      initialValues={initialData}
      hasCard={false}
      buttonText={strings.FormListAddItem}
      onSecondarySubmit={onRemove}
      secondaryButtonText={strings.FormListRemoveItem}
      showSecondarySubmitButton={canRemove}
      onChange={handleChange}
      onSubmit={onSubmit}
      validateOnChange
      validateOnMount={showErrors}
      onChangeDebounceTime={0}
      showErrors={showErrors}
      showSubmitButton={canAdd}
      buttonVariant="outlined"
      error={isDuplicated ? 'item is duplicated' : undefined}
    />
  );
};
